import { z } from 'zod';
import type { BlindtestState, FaceMode, FacesState, Moment, PhotoWallSettings, QueueSource } from '@karozik/engine';

// ---------------------------------------------------------------------------
// Constants shared by every client.

export const REACTIONS = ['🔥', '💃', '❤️', '😂', '🙌', '🎉'] as const;
export type Reaction = (typeof REACTIONS)[number];

export const AVATARS = [
  '🦊', '🐼', '🐸', '🦁', '🐯', '🐨', '🐙', '🦄', '🐝', '🦉', '🐧', '🐢',
  '🦖', '🐬', '🦩', '🐻', '🐰', '🐱', '🐶', '🦋', '🌵', '🍕', '🎸', '🪩',
] as const;

export const COLORS = [
  '#ff3cac', '#784ba0', '#2b86c5', '#00d2ff', '#3ae374', '#fff200',
  '#ff9f1a', '#ff4d4d', '#c56cf0', '#17c0eb', '#7bed9f', '#ffb8b8',
] as const;

export const PSEUDO_MAX = 20;

/** Têtes Mêlées selfies: cropped on the phone to this size, eyes on `FACE_EYES_Y`. */
export const FACE_WIDTH = 360;
export const FACE_HEIGHT = 450;
export const FACE_EYES_Y = 190;
/** Upper bound for one selfie JPEG (a 360×450 photo weighs 25–60 kB). */
export const FACE_MAX_BYTES = 150 * 1024;
/** Face landmarks sent with a selfie: MediaPipe's 468 mesh points, as x,y pairs in the 360×450 frame. */
export const FACE_LANDMARKS = 468;
export const DEDICATION_MAX = 80;

/**
 * Shared photo library: the phone shrinks each photo to `PHOTO_MAX_SIDE` and
 * makes its thumbnail (shortest side `PHOTO_THUMB_SIDE`) before sending both.
 */
export const PHOTO_MAX_SIDE = 2048;
export const PHOTO_THUMB_SIDE = 400;
/** Upper bounds per upload (a 2048 px JPEG weighs 300–900 kB, its thumbnail 20–60 kB). */
export const PHOTO_MAX_BYTES = 4 * 1024 * 1024;
export const PHOTO_THUMB_MAX_BYTES = 300 * 1024;
export const PHOTOS_PER_GUEST = 300;

// ---------------------------------------------------------------------------
// Public views (what the server broadcasts).

export interface PublicTrack {
  id: string;
  title: string;
  artist: string;
  album: string | null;
  year: number | null;
  durationMs: number;
  artworkUrl: string | null;
  explicit: boolean;
  /** Only sent to staff (player/admin), and in search results. */
  previewUrl?: string | null;
}

export interface PublicGuest {
  id: string;
  pseudo: string;
  emoji: string;
  color: string;
}

export interface PublicQueueItem {
  id: string;
  track: PublicTrack;
  proposer: PublicGuest | null;
  source: QueueSource;
  dedication: string | null;
  votes: number;
  voters: string[];
  pinned: boolean;
}

export interface PublicNowPlaying extends PublicQueueItem {
  startedAt: number;
  skipVoters: string[];
  skipNeeded: number;
}

export interface PublicPlayed extends PublicQueueItem {
  startedAt: number;
}

export interface PublicPhase {
  id: string;
  name: string;
  emoji: string;
}

export interface PublicBlindtest {
  id: string;
  round: number;
  totalRounds: number;
  stage: BlindtestState['stage'];
  stageEndsAt: number;
  choices: string[] | null;
  answered: string[];
  /** Revealed once the round is over. */
  correctIndex: number | null;
  revealTrack: PublicTrack | null;
  lastPoints: Record<string, number>;
  leaderboard: { guest: PublicGuest; points: number }[];
  /** Staff only: what the player must play during the round. */
  clip?: { trackId: string; previewUrl: string | null; offsetMs: number; durationMs: number } | null;
}

export interface PublicFaces {
  id: string;
  round: number;
  totalRounds: number;
  stage: FacesState['stage'];
  stageEndsAt: number;
  /** When the current picture started un-pixelating (server clock), and for how long. */
  startedAt: number | null;
  playMs: number;
  /** Then still this long to answer, picture fully sharp (answers score the minimum). */
  sharpMs: number;
  mode: FaceMode | null;
  /** Rendering knobs set from the admin. */
  render: { ratio: number; caricature: number; pixelStart: number };
  /**
   * The two selfies of the round and their landmarks (`{ a, b }` JSON, each a flat
   * x,y array or null). The URLs carry an opaque token, never who is pictured.
   */
  images: { a: string; b: string; landmarks: string } | null;
  /** Two to four pairs of guests. */
  choices: [PublicGuest, PublicGuest][] | null;
  answered: string[];
  /** Revealed once the round is over. */
  correctIndex: number | null;
  lastPoints: Record<string, number>;
  leaderboard: { guest: PublicGuest; points: number }[];
}

export type EventStatus = 'upcoming' | 'live' | 'ended';

export interface PublicEvent {
  id: string;
  name: string;
  startsAt: number;
  endsAt: number;
  status: EventStatus;
  joinUrl: string;
  wifiSsid: string | null;
}

export interface PublicState {
  event: PublicEvent;
  provider: MusicProviderName;
  nowPlaying: PublicNowPlaying | null;
  queue: PublicQueueItem[];
  recent: PublicPlayed[];
  phase: PublicPhase;
  phases: PublicPhase[];
  phaseOverridden: boolean;
  paused: boolean;
  volume: number;
  moment: Moment | null;
  mic: { active: boolean; speaker: string | null };
  heat: number;
  guestsOnline: number;
  blindtest: PublicBlindtest | null;
  faces: PublicFaces | null;
  /** Guests who have a selfie for Têtes Mêlées. */
  faceCount: number;
  /** Photos in the shared library: clients reload the list when it changes. */
  photoCount: number;
  serverTime: number;
  /** Staff only. */
  staff?: {
    fadeMs: number;
    ducking: { auto: boolean; threshold: number; level: number };
    skipVoteEnabled: boolean;
    playerOnline: boolean;
    showLyrics: boolean;
    photoWall: PhotoWallSettings;
  };
}

export type MusicProviderName = 'preview' | 'apple';

export interface GuestMe extends PublicGuest {
  eventId: string;
}

export interface JoinInfo {
  event: Pick<PublicEvent, 'id' | 'name' | 'startsAt' | 'endsAt' | 'status' | 'wifiSsid'>;
  me: GuestMe | null;
}

export interface SearchTrack extends PublicTrack {
  /** Set for a guest's search when the track cannot be proposed right now. */
  unavailable?: 'playing' | 'queued' | 'played';
}

export interface SearchResponse {
  tracks: SearchTrack[];
  /** True when the remote catalog was unavailable and only local results are shown. */
  limited: boolean;
}

/** One photo of the shared library. The URLs hold the photo's random id, nothing else. */
export interface PublicPhoto {
  id: string;
  /** Who took it; null once the photo outlives its guest. */
  guest: PublicGuest | null;
  createdAt: number;
  thumbUrl: string;
  url: string;
  /** Size of the full photo, in bytes. */
  size: number;
}

export interface PhotoList {
  /** Newest first. */
  photos: PublicPhoto[];
  /** The guest reading the list (their own photos can be deleted), or null. */
  me: string | null;
  canUpload: boolean;
  zipUrl: string;
}

/** Lyrics of one track, for the big screen (staff only). */
export interface TrackLyrics {
  trackId: string;
  /** Lines with their start time in ms, or null when no synced version was found. */
  synced: { t: number; text: string }[] | null;
  /** True when only unsynced text exists (not shown on screen). */
  plainOnly: boolean;
  instrumental: boolean;
}

export interface ReactionEvent {
  emoji: Reaction;
  guest: PublicGuest | null;
}

export interface Toast {
  kind: 'success' | 'info' | 'error';
  message: string;
}

export interface PlayerFallback {
  tracks: PublicTrack[];
}

/** Sent by the player when it reconnects after playing on its own. */
export interface PlayerResync {
  played: { trackId: string; startedAt: number }[];
  current: { trackId: string; startedAt: number } | null;
}

export type Ack<T = undefined> = (res: { ok: true; data?: T } | { ok: false; error: string }) => void;

// ---------------------------------------------------------------------------
// Client → server payloads (validated with zod on the server).

export const proposeSchema = z.object({
  trackId: z.string().min(1).max(40),
  dedication: z.string().trim().max(DEDICATION_MAX).optional().nullable(),
});
export const voteSchema = z.object({ itemId: z.string().min(1), value: z.boolean() });
export const reactSchema = z.object({ emoji: z.enum(REACTIONS) });
export const answerSchema = z.object({ choice: z.number().int().min(0).max(3) });
/** A selfie upload: a JPEG data URL, already cropped on the phone. */
export const faceUploadSchema = z.object({
  image: z.string().startsWith('data:image/jpeg;base64,').max(Math.ceil((FACE_MAX_BYTES * 4) / 3) + 64),
  landmarks: z
    .array(z.number().finite().min(-500).max(1000))
    .length(FACE_LANDMARKS * 2)
    .nullable()
    .optional(),
});
export const playerEndedSchema = z.object({ itemId: z.string() });
export const playerErrorSchema = z.object({ itemId: z.string().nullable(), message: z.string().max(500) });
export const playerResyncSchema = z.object({
  played: z.array(z.object({ trackId: z.string(), startedAt: z.number() })).max(200),
  current: z.object({ trackId: z.string(), startedAt: z.number() }).nullable(),
});

export const momentSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('birthday'), name: z.string().trim().min(1).max(40), trackId: z.string().optional() }),
  z.object({ type: z.literal('slows'), count: z.number().int().min(1).max(5).default(3) }),
  z.object({ type: z.literal('collective'), trackId: z.string().optional(), label: z.string().max(40).optional() }),
  z.object({ type: z.literal('photo'), seconds: z.number().int().min(5).max(60).default(10) }),
  z.object({ type: z.literal('lastdance'), trackId: z.string().optional() }),
  z.object({ type: z.literal('announce'), text: z.string().trim().min(1).max(120) }),
]);
export type MomentRequest = z.infer<typeof momentSchema>;

export const controlSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('play') }),
  z.object({ type: z.literal('pause') }),
  z.object({ type: z.literal('skip') }),
  z.object({ type: z.literal('volume'), value: z.number().min(0).max(1) }),
  z.object({ type: z.literal('remove'), itemId: z.string() }),
  z.object({ type: z.literal('pin'), itemId: z.string() }),
  z.object({ type: z.literal('unpin'), itemId: z.string() }),
  z.object({ type: z.literal('add'), trackId: z.string(), pin: z.boolean().default(false) }),
  z.object({ type: z.literal('phase'), phaseId: z.string().nullable() }),
  z.object({ type: z.literal('moment'), moment: momentSchema }),
  z.object({ type: z.literal('momentClear') }),
  z.object({ type: z.literal('mic'), active: z.boolean(), speaker: z.string().trim().max(40).nullable().optional() }),
  z.object({ type: z.literal('blindtestStart'), rounds: z.number().int().min(1).max(15).default(5) }),
  z.object({ type: z.literal('blindtestStop') }),
  z.object({ type: z.literal('facesStart'), rounds: z.number().int().min(1).max(15).default(5) }),
  z.object({ type: z.literal('facesStop') }),
  z.object({ type: z.literal('banTrack'), trackId: z.string() }),
  z.object({ type: z.literal('banGuest'), guestId: z.string() }),
  z.object({ type: z.literal('lyrics'), show: z.boolean() }),
  z.object({
    type: z.literal('photoWall'),
    enabled: z.boolean().optional(),
    mode: z.enum(['continuous', 'interlude']).optional(),
    seconds: z.number().int().min(3).max(30).optional(),
    gapSeconds: z.number().int().min(5).max(600).optional(),
    newFirst: z.boolean().optional(),
    showAuthor: z.boolean().optional(),
  }),
]);
export type ControlCommand = z.infer<typeof controlSchema>;

/** What the kiosk animator (PIN) is allowed to do — no moderation, no volume games. */
export const ANIMATOR_COMMANDS: ControlCommand['type'][] = [
  'skip',
  'moment',
  'momentClear',
  'mic',
  'blindtestStart',
  'blindtestStop',
  'facesStart',
  'facesStop',
  'phase',
];

// ---------------------------------------------------------------------------
// Socket.IO typed events.

export interface ServerToClientEvents {
  state: (s: PublicState) => void;
  reaction: (r: ReactionEvent) => void;
  toast: (t: Toast) => void;
  me: (me: GuestMe | null) => void;
  'player:fallback': (f: PlayerFallback) => void;
}

export interface ClientToServerEvents {
  propose: (p: z.input<typeof proposeSchema>, ack: Ack) => void;
  vote: (p: z.infer<typeof voteSchema>, ack: Ack) => void;
  skipVote: (p: { value: boolean }, ack: Ack) => void;
  react: (p: z.infer<typeof reactSchema>) => void;
  'blindtest:answer': (p: z.infer<typeof answerSchema>, ack: Ack) => void;
  'faces:answer': (p: z.infer<typeof answerSchema>, ack: Ack) => void;
  control: (c: z.input<typeof controlSchema>, ack: Ack) => void;
  'player:ended': (p: z.infer<typeof playerEndedSchema>) => void;
  'player:error': (p: z.infer<typeof playerErrorSchema>) => void;
  'player:resync': (p: PlayerResync) => void;
}

/** Socket handshake `auth` payload. Cookies authenticate guests and admins on their own hosts. */
export type SocketAuth =
  | { role: 'guest'; joinToken: string; guestToken?: string }
  | { role: 'kiosk'; kioskToken: string; guestToken?: string; animatorToken?: string }
  | { role: 'player' | 'admin'; eventId: string };

export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function appleMusicUrl(trackId: string): string {
  return `https://music.apple.com/fr/song/${trackId}`;
}

export function wifiQrPayload(w: { ssid: string; password: string; security: 'WPA' | 'WEP' | 'nopass' }): string {
  const esc = (s: string) => s.replace(/([\\;,:"])/g, '\\$1');
  return `WIFI:T:${w.security};S:${esc(w.ssid)};P:${w.security === 'nopass' ? '' : esc(w.password)};;`;
}
